import { useState, memo } from "react";
import { Col, Button, Image } from "react-bootstrap";
import { User } from "../../types/Types";
import { getCountryFlagUrl } from "../../utils/countryFlag";
import {
  getAvatarUrl,
  getInitials,
  getRole,
  getMembershipBadgeKey,
  getConnectionPermission,
} from "../../utils/connectionUserUtils";
import { ConnectionUpgradePrompt } from "../Connections/ConnectionUpgradePrompt";
import { useTranslation } from "react-i18next";

export type ConnectionState = "connected" | "sent" | "received" | "none";

export interface AttendeeWithState {
  user: User;
  actionUser: User;
  connectionState: ConnectionState;
}

export interface EventAttendeeCardProps {
  attendee: AttendeeWithState;
  isCurrentUser: boolean;
  /** Explorer login without paid membership — profile only for other attendees. */
  isExplorerLogin?: boolean;
  currentUserMembership: string | undefined;
  onViewProfile: (user: User) => void;
  onMessage: (id: number) => void;
  onConnect: (user: User) => void;
  onCancelRequest: (id: number) => void;
  onAccept: (id: number, user: User) => void;
  onIgnore: (id: number) => void;
  onRemoveConnection: (user: User) => void;
  sendConnectionId: number | null;
  cancellingId: number | null;
}

function EventAttendeeCard({
  attendee,
  isCurrentUser,
  isExplorerLogin = false,
  currentUserMembership,
  onViewProfile,
  onMessage,
  onConnect,
  onCancelRequest,
  onAccept,
  onIgnore,
  onRemoveConnection,
  sendConnectionId,
  cancellingId,
}: EventAttendeeCardProps) {
  const { t } = useTranslation();
  const { user, actionUser, connectionState } = attendee;
  const [avatarError, setAvatarError] = useState(false);

  const avatarUrl = getAvatarUrl(user);
  const initials = getInitials(user);
  const role = getRole(user);
  const membershipBadgeKey = getMembershipBadgeKey(user);
  const locationLabel = user.city?.trim() || user.country?.trim() || "";
  const locationFlagUrl = getCountryFlagUrl(user.country, user.city);
  const showInitials = !avatarUrl || avatarError;

  const profileButton = (
    <Button
      variant="light"
      className="rounded-2 fw-medium text-dark border-0 shadow-sm d-flex align-items-center justify-content-center flex-fill action-button"
      onClick={(e) => {
        e.stopPropagation();
        onViewProfile(user);
      }}
    >
      {t("connections.viewProfile")}
    </Button>
  );

  const { canConnect, upgradeKind } = getConnectionPermission(
    currentUserMembership,
    {
      membershipName: user.membershipName || actionUser.membershipName,
      explorerAttendeeType:
        user.explorerAttendeeType ?? actionUser.explorerAttendeeType,
    }
  );
  const targetMembershipName =
    user.membershipName?.trim() || actionUser.membershipName?.trim() || "";
  const shouldShowConnectFallback =
    !targetMembershipName || upgradeKind === "none";

  let action: React.ReactNode;
  if (isCurrentUser) {
    action = (
      <div className="d-flex flex-row flex-sm-column gap-2 flex-shrink-0 action-button-group align-items-center">
        <span className="badge bg-secondary text-nowrap">{t("connections.you")}</span>
        {profileButton}
      </div>
    );
  } else if (isExplorerLogin) {
    action = (
      <div className="d-flex flex-row flex-sm-column gap-2 flex-shrink-0 action-button-group align-items-center">
        {profileButton}
      </div>
    );
  } else if (connectionState === "connected") {
    action = (
      <div className="d-flex flex-row flex-sm-column gap-2 flex-shrink-0 action-button-group">
        <Button
          variant="primary"
          className="rounded-2 fw-medium text-white border-0 shadow-sm flex-fill connect-btn action-button"
          onClick={() => onMessage(actionUser.id)}
        >
          {t("connections.message")}
        </Button>
        {profileButton}
      </div>
    );
  } else if (connectionState === "sent") {
    action = (
      <div className="d-flex flex-row flex-sm-column gap-2 flex-shrink-0 action-button-group">
        {profileButton}
        <Button
          variant="outline-danger"
          className="rounded-2 fw-medium cancel-request-btn flex-fill action-button"
          onClick={() => onCancelRequest(actionUser.id)}
          disabled={cancellingId === actionUser.id}
        >
          {cancellingId === actionUser.id ? t("connections.cancelling") : t("connections.cancelRequest")}
        </Button>
      </div>
    );
  } else if (connectionState === "received") {
    action = (
      <div className="d-flex flex-row flex-sm-column gap-2 flex-shrink-0 action-button-group">
        <Button
          variant="primary"
          className="rounded-2 fw-medium border-0 shadow-sm text-white action-button"
          style={{ background: "linear-gradient(90deg, #2366CD 0%, #3A97A4 100%)" }}
          onClick={() => onAccept(actionUser.id, actionUser)}
        >
          {t("connections.accept")}
        </Button>
        <Button
          variant="light"
          className="rounded-2 fw-medium text-dark border-0 shadow-sm action-button"
          onClick={() => onIgnore(actionUser.id)}
        >
          {t("connections.ignore")}
        </Button>
      </div>
    );
  } else {
    if (canConnect || shouldShowConnectFallback) {
      action = (
        <div className="d-flex flex-row flex-sm-column gap-2 flex-shrink-0 action-button-group">
          {profileButton}
          <Button
            variant="primary"
            className="rounded-2 fw-medium text-white border-0 shadow-sm flex-fill connect-btn action-button"
            disabled={sendConnectionId === actionUser.id}
            onClick={() => onConnect(actionUser)}
          >
            {sendConnectionId === actionUser.id ? t("connections.sending") : t("connections.connect")}
          </Button>
        </div>
      );
    } else {
      action = (
        <div className="d-flex flex-row flex-sm-column gap-2 flex-shrink-0 action-button-group">
          {profileButton}
          <div className="rounded-2 fw-medium border-0 shadow-sm d-flex align-items-center flex-fill text-start text-secondary upgrade-message">
            <ConnectionUpgradePrompt kind={upgradeKind} variant="compact" />
          </div>
        </div>
      );
    }
  }

  return (
    <Col xs={12} className="mb-3">
      <div className="card shadow-sm border rounded-3">
        <div className="card-body d-flex flex-column flex-sm-row gap-3 align-items-sm-start">
          <div
            className="d-flex gap-3 flex-grow-1 min-w-0"
            onClick={() => onViewProfile(user)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onViewProfile(user);
            }}
          >
            <div className="flex-shrink-0">
              {showInitials ? (
                <div className="d-flex align-items-center justify-content-center text-white fw-bold rounded avatar-initials avatar-size">
                  {initials}
                </div>
              ) : (
                <Image
                  src={avatarUrl}
                  alt={`${user.firstName} ${user.lastName}`}
                  rounded
                  loading="lazy"
                  onError={() => setAvatarError(true)}
                  className="avatar-image avatar-size"
                />
              )}
            </div>
            <div className="min-w-0 flex-grow-1">
              <div className="fw-semibold text-dark pb-1">{`${user.firstName} ${user.lastName}`}</div>
              {role && <div className="text-muted small pb-1">{role}</div>}
              <div className="d-flex flex-wrap gap-2 pt-1">
                {membershipBadgeKey ? (
                  <span className="text-primary small">{t(membershipBadgeKey)}</span>
                ) : (
                  user.membershipName && (
                    <span className="text-primary small">{user.membershipName}</span>
                  )
                )}
                {locationLabel && (
                  <span className="text-muted small d-inline-flex align-items-center gap-1">
                    {locationFlagUrl && (
                      <img
                        src={locationFlagUrl}
                        alt=""
                        width={20}
                        height={15}
                        className="rounded object-fit-cover"
                      />
                    )}
                    {locationLabel}
                  </span>
                )}
              </div>
            </div>
          </div>
          {action}
        </div>
      </div>
    </Col>
  );
}

export default memo(EventAttendeeCard);
